import type { OrderListItem } from '@agos/api-client/model'
import { T, formatMoney, tt } from '@agos/shared'
import { cn, readableTextColor } from '@agos/ui'
import { Users } from 'lucide-react'
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import {
  PLANE_HEIGHT,
  PLANE_WIDTH,
  STATUS_BG,
  STATUS_RING,
  clampToPlane,
  statusForOrder,
} from './table-status'
import type { RestaurantTable } from './types'

/**
 * The floor plan.
 *
 * Tiles are positioned as percentages of a fixed 1000 × 600 plane, so the same
 * layout drawn once on a laptop reads the same on the till's tablet and on a
 * phone. §14.9 — this is a POS surface first.
 *
 * A tile is both draggable and clickable, which is one gesture too many for
 * `onClick`: the pointer handlers track whether the pointer actually moved, and
 * only a press that did not move counts as a tap.
 */

export interface FloorCanvasProps {
  tables: RestaurantTable[]
  editMode: boolean
  selectedId: number | null
  onSelect: (tableId: number | null) => void
  positionOf: (table: RestaurantTable) => { x: number; y: number }
  onDragTo: (tableId: number, position: { x: number; y: number }) => void
  onTileTap: (table: RestaurantTable) => void
  ordersByTableName: Map<string, OrderListItem>
}

interface DragState {
  id: number
  offsetX: number
  offsetY: number
  moved: boolean
}

export function FloorCanvas({
  tables,
  editMode,
  selectedId,
  onSelect,
  positionOf,
  onDragTo,
  onTileTap,
  ordersByTableName,
}: FloorCanvasProps) {
  const plane = useRef<HTMLDivElement | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)

  /** Pointer position in plane units, whatever the element is scaled to. */
  const toPlane = (event: ReactPointerEvent) => {
    const bounds = plane.current?.getBoundingClientRect()
    if (!bounds) return null
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * PLANE_WIDTH,
      y: ((event.clientY - bounds.top) / bounds.height) * PLANE_HEIGHT,
    }
  }

  const beginDrag = (event: ReactPointerEvent, table: RestaurantTable) => {
    if (!editMode) return
    event.stopPropagation()
    const point = toPlane(event)
    if (!point) return
    const current = positionOf(table)
    setDrag({
      id: table.id,
      offsetX: point.x - current.x,
      offsetY: point.y - current.y,
      moved: false,
    })
    ;(event.target as Element).setPointerCapture?.(event.pointerId)
  }

  const continueDrag = (event: ReactPointerEvent) => {
    if (!drag) return
    const point = toPlane(event)
    const table = tables.find((candidate) => candidate.id === drag.id)
    if (!point || !table) return
    onDragTo(
      drag.id,
      clampToPlane(point.x - drag.offsetX, point.y - drag.offsetY, table.width, table.height),
    )
    if (!drag.moved) setDrag((current) => (current ? { ...current, moved: true } : current))
  }

  const endDrag = (event: ReactPointerEvent) => {
    if (!drag) return
    const table = tables.find((candidate) => candidate.id === drag.id)
    // A press that never moved is a tap, not a drag.
    if (table && !drag.moved) onTileTap(table)
    setDrag(null)
    ;(event.target as Element).releasePointerCapture?.(event.pointerId)
  }

  return (
    <div
      ref={plane}
      onPointerMove={continueDrag}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onClick={() => {
        if (editMode) onSelect(null)
      }}
      className="relative w-full rounded-xl bg-muted/30 border border-dashed border-border overflow-hidden select-none touch-none"
      style={{ aspectRatio: `${PLANE_WIDTH / PLANE_HEIGHT}` }}
    >
      {tables.map((table) => {
        const { x, y } = positionOf(table)
        const order = editMode ? null : (ordersByTableName.get(table.name) ?? null)
        const status = statusForOrder(order)
        const isSelected = table.id === selectedId

        return (
          <button
            key={table.id}
            type="button"
            // The status is on the tile as a ring and a background; naming it makes the same fact
            // reachable to a test, which otherwise has to click a table to find out whether it is free.
            data-table={table.name}
            data-table-status={editMode ? 'edit' : status}
            onPointerDown={(event) => beginDrag(event, table)}
            onClick={(event) => {
              event.stopPropagation()
              if (!editMode) onTileTap(table)
            }}
            className={cn(
              'absolute flex flex-col items-center justify-center text-center font-bold transition-shadow',
              table.shape === 'circle' ? 'rounded-full' : 'rounded-xl',
              editMode
                ? isSelected
                  ? 'ring-2 ring-primary shadow-lg z-10'
                  : 'hover:ring-2 hover:ring-primary/40'
                : STATUS_RING[status],
              !editMode && STATUS_BG[status],
              editMode ? 'cursor-move' : 'cursor-pointer',
            )}
            style={{
              left: `${(x / PLANE_WIDTH) * 100}%`,
              top: `${(y / PLANE_HEIGHT) * 100}%`,
              width: `${(table.width / PLANE_WIDTH) * 100}%`,
              height: `${(table.height / PLANE_HEIGHT) * 100}%`,
              background: table.color ?? undefined,
              // A table coloured pale yellow needs black text, whatever the theme.
              color: table.color ? readableTextColor(table.color) : 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
            }}
          >
            <span className="text-sm leading-tight">{table.name}</span>
            {order ? (
              <>
                <span className="text-[10px] opacity-80">{formatMoney(order.total)}</span>
                <span className="text-[9px] opacity-70">
                  {order.items?.length ?? 0} {tt('pol.')}
                </span>
                {order.employeeName && (
                  <span
                    className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold shadow-md whitespace-nowrap max-w-[90%] truncate"
                    title={`${tt('Obsluha')}: ${order.employeeName}`}
                  >
                    <Users className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="truncate">{order.employeeName.split(' ')[0]}</span>
                  </span>
                )}
              </>
            ) : (
              <span className="text-[10px] opacity-70 flex items-center gap-0.5">
                <Users className="w-2.5 h-2.5" /> {table.capacity}
              </span>
            )}
          </button>
        )
      })}
      {tables.length === 0 && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          <T>Žiadne stoly v tejto zóne</T>
        </p>
      )}
    </div>
  )
}
